import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/_components/button";
import { Card, CardContent } from "@/components/_components/card";
import { Skeleton } from "@/components/_components/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/_components/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useErrorHandler } from "@/hooks/useErrorHandler";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/_components/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/_components/alert-dialog";
import { AGENT_TYPE, AGENT_TYPE_LABELS } from "@/constants/constants";
import { API_CONFIG } from "@/constants/api";
import { Plus, Search, Info, RotateCcw, Edit, Trash2, Settings, BarChart3 } from "lucide-react";
import { customInstance } from "@/lib/axios-instance";
import { trimObjectKeysAndValues } from "@/lib/utils/data-trimmer";
import toast from "react-hot-toast";
import { usePageHeader } from "@/contexts/page-header-context";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/_components/badge";
import CommunicationIntegrationForm from "./_components/CommunicationIntegrationForm";
import { PageAccessGuard } from "@/components/auth/PageAccessGuard";
import { useAuthorities } from "@/hooks/useAuthorities";

interface CommunicationIntegration {
  id?: number;
  agentType: string;
  intType: string;
  countryCode: string;
  phoneNo: string;
  details: Record<string, any>;
  detailsAsString?: string;
}

interface SearchFilter {
  agentType: string;
  intType: string;
  phoneNo: string;
}

const INTEGRATION_TYPES = [
  { key: "awsConnect", value: "AWS Connect" },
  { key: "twilio", value: "Twilio" },
];

const CommunicationIntegrationPage: React.FC = () => {
  return (
    <PageAccessGuard authority="ADMIN_PHONE_MGMT_VIEW">
      <CommunicationIntegrationContent />
    </PageAccessGuard>
  );
};

const CommunicationIntegrationContent: React.FC = () => {
  const { setHeaderData } = usePageHeader();
  const { hasAuthority } = useAuthorities();
  const { handleError } = useErrorHandler();
  const [integrations, setIntegrations] = useState<CommunicationIntegration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchFilter, setSearchFilter] = useState<SearchFilter>({
    agentType: "all",
    intType: "all",
    phoneNo: "",
  });

  // Dialog states
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState<Record<string, any>>({});
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState<CommunicationIntegration | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingIntegration, setDeletingIntegration] = useState<CommunicationIntegration | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const hasFetched = useRef(false);
  const canUpdate = hasAuthority('ADMIN_PHONE_MGMT_UPDATE');

  const fetchIntegrations = async (agentType?: string, intType?: string, phoneNo?: string) => {
    try {
      setIsLoading(true);

      // Prepare search data, converting "all" to empty string for API
      const searchData = {
        agentType: agentType === "all" || !agentType ? "" : agentType,
        intType: intType === "all" || !intType ? "" : intType,
        phoneNo: phoneNo || "",
      };

      // Trim all keys and values before sending to backend
      const trimmedSearchData = trimObjectKeysAndValues(searchData);

      const response = await customInstance<CommunicationIntegration[]>({
        url: API_CONFIG.ENDPOINTS.COMMUNICATION_SEARCH,
        method: "POST",
        data: trimmedSearchData,
      });
      setIntegrations(response.data || []);
    } catch (error: any) {
      console.error("Error searching communication integrations:", error);
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchIntegrations();
    }
  }, []);

  const handleSearch = () => {
    fetchIntegrations(searchFilter.agentType, searchFilter.intType, searchFilter.phoneNo);
  };

  const handleReset = () => {
    setSearchFilter({
      agentType: "all",
      intType: "all",
      phoneNo: "",
    });
    fetchIntegrations();
    toast.success("Form reset successfully");
  };

  const handleDetailsClick = (details: Record<string, any>) => {
    setSelectedDetails(details);
    setDetailsDialogOpen(true);
  };

  const handleAddClick = () => {
    setEditingIntegration(null);
    setFormDialogOpen(true);
  };

  const handleEditClick = (integration: CommunicationIntegration) => {
    setEditingIntegration(integration);
    setFormDialogOpen(true);
  };

  const handleDeleteClick = (integration: CommunicationIntegration) => {
    setDeletingIntegration(integration);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingIntegration) return;
    try {
      setIsDeleting(true);
      await customInstance({
        url: `${API_CONFIG.ENDPOINTS.COMMUNICATION_INTEGRATION}/id/${deletingIntegration.id}`,
        method: "DELETE",
      });
      toast.success("Communication integration deleted successfully");
      setDeleteDialogOpen(false);
      setDeletingIntegration(null);
      fetchIntegrations(searchFilter.agentType, searchFilter.intType, searchFilter.phoneNo);
    } catch (error: any) {
      console.error("Error deleting phone number mapping:", error);
      handleError(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFormSuccess = () => {
    setFormDialogOpen(false);
    setEditingIntegration(null);
    fetchIntegrations(searchFilter.agentType, searchFilter.intType, searchFilter.phoneNo); // Refresh the list
  };

  // Set page header data
  useEffect(() => {
    setHeaderData({
      stats: integrations.length > 0 ? {
        count: integrations.length,
        label: 'integration',
        icon: <BarChart3 className="h-4 w-4" />
      } : undefined,
      actions: canUpdate ? (
        <Button
          onClick={handleAddClick}
          className="h-9 px-2 bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 font-semibold text-sm rounded-md"
        >
          <Plus className="h-4 w-3" />
          Add Integration
        </Button>
      ) : undefined
    });

    return () => {
      setHeaderData(null);
    };
  }, [integrations, canUpdate, setHeaderData]);

  if (isLoading && !hasFetched.current) {
    return (
      <div className="bg-background">
        <div className="max-w-7xl mx-auto px-2 sm:px-2 lg:px-2 py-4">
          <div className="space-y-4">
            <Card className="shadow-md border-border bg-card hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-6">
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
            <Card className="shadow-md border-border bg-card">
              <CardContent className="p-6">
                <Skeleton className="h-64 w-full" />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  const totalIntegrations = integrations.length;

  return (
    <div className="bg-background">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-2 sm:px-2 lg:px-2 py-4">
        <div className="space-y-4">

          {/* Search & Filter Section - Modern Clean Design */}
          <div className="bg-card border border-border/50 rounded-lg mb-2">
            {/* Compact Header */}
            <div className="px-4 pt-3 pb-2 border-b border-border/50">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 bg-primary/10 rounded-md">
                  <Search className="h-4 w-4 text-primary" />
                </div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-foreground leading-tight">
                    Search & Filter
                  </h3>
                  <span className="text-sm text-muted-foreground">•</span>
                  <p className="text-sm text-muted-foreground">
                    Find integrations by agent type, integration type, and phone number
                  </p>
                </div>
              </div>
            </div>

            {/* Search & Filter Controls */}
            <div className="px-4 pb-4 pt-4">
              <div className="flex items-end gap-3 flex-wrap">
                {/* Agent Type */}
                <div className="space-y-1.5 flex-1 min-w-[150px]">
                  <label htmlFor="agentType" className="block text-sm font-semibold text-foreground">
                    Agent Type
                  </label>
                  <Select
                    value={searchFilter.agentType}
                    onValueChange={(value) =>
                      setSearchFilter((prev) => ({ ...prev, agentType: value }))
                    }
                  >
                    <SelectTrigger className="h-9 bg-background border border-border hover:border-primary/60 transition-colors focus:ring-2 focus:ring-primary/20">
                      <SelectValue placeholder="Select agent type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Agent Types</SelectItem>
                      {AGENT_TYPE.map((type) => (
                        <SelectItem key={type} value={type}>
                          {AGENT_TYPE_LABELS[type]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Integration Type */}
                <div className="space-y-1.5 flex-1 min-w-[150px]">
                  <label htmlFor="intType" className="block text-sm font-semibold text-foreground">
                    Integration Type
                  </label>
                  <Select
                    value={searchFilter.intType}
                    onValueChange={(value) =>
                      setSearchFilter((prev) => ({ ...prev, intType: value }))
                    }
                  >
                    <SelectTrigger className="h-9 bg-background border border-border hover:border-primary/60 transition-colors focus:ring-2 focus:ring-primary/20">
                      <SelectValue placeholder="Select integration type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Integration Types</SelectItem>
                      {INTEGRATION_TYPES.map((type) => (
                        <SelectItem key={type.key} value={type.key}>
                          {type.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Phone Number */}
                <div className="space-y-1.5 flex-1 min-w-[150px]">
                  <label htmlFor="phoneNo" className="block text-sm font-semibold text-foreground">
                    Phone Number
                  </label>
                  <Input
                    id="phoneNo"
                    type="text"
                    placeholder="Search by phone number"
                    value={searchFilter.phoneNo}
                    onChange={(e) =>
                      setSearchFilter((prev) => ({ ...prev, phoneNo: e.target.value }))
                    }
                    className="h-9 bg-background border border-border hover:border-primary/60 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleSearch}
                    disabled={isLoading}
                    className="h-9 px-4 bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg transition-all duration-200 font-semibold text-sm rounded-md"
                  >
                    {isLoading ? (
                      <>
                        <Search className="h-4 w-4 mr-1.5 animate-pulse" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <Search className="h-4 w-4 mr-1.5" />
                        Search
                      </>
                    )}
                  </Button>

                  <Button
                    onClick={handleReset}
                    className="h-9 px-4 bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg transition-all duration-200 font-semibold text-sm rounded-md"
                  >
                    <RotateCcw className="h-4 w-4 mr-1.5" />
                    Reset
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Results Table */}
          {integrations.length === 0 ? (
            <Card className="shadow-md border-border bg-card hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-12 text-center">
                <div className="max-w-md mx-auto">
                  <div className="w-16 h-16 mx-auto mb-4 bg-muted/30 rounded-full flex items-center justify-center">
                    <Search className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">No Communication Integrations Found</h3>
                  <p className="text-muted-foreground mb-4">
                    {searchFilter.agentType !== "all" || searchFilter.intType !== "all" || searchFilter.phoneNo
                      ? "No integrations found matching your search criteria"
                      : "No communication integrations yet. Create one to get started."}
                  </p>
                  <div className="flex gap-2 justify-center">
                    {(searchFilter.agentType !== "all" || searchFilter.intType !== "all" || searchFilter.phoneNo) && (
                      <Button
                        variant="outline"
                        onClick={handleReset}
                        className="text-primary border-primary hover:bg-primary/10 shadow-sm hover:shadow-md transition-all duration-200"
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Clear Filters
                      </Button>
                    )}
                    {canUpdate && (
                      <Button
                        onClick={handleAddClick}
                        className="bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg transition-all duration-200"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Integration
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              <Card className="border border-border/50 bg-card backdrop-blur-sm">
                <CardContent className="p-4">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="p-2 bg-primary/10 rounded-lg transition-colors hover:bg-primary/20">
                      <Settings className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <h2 className="text-xl font-bold text-foreground">
                        Phone Number Mapping Results
                      </h2>
                      <Badge variant="secondary" className="text-xs font-medium">
                        {totalIntegrations} {totalIntegrations === 1 ? 'record' : 'records'}
                      </Badge>
                      <span className="text-muted-foreground/60">•</span>
                      <p className="text-sm text-muted-foreground">
                        Manage your communication integrations and track configurations
                      </p>
                    </div>
                  </div>

                  <div className="w-full">
                    <div className="overflow-hidden rounded-lg border border-border/50">
                      <Table>
                        <TableHeader className="sticky top-0 z-10 bg-muted/95 backdrop-blur supports-[backdrop-filter]:bg-muted/60">
                          <TableRow>
                            <TableHead className="font-semibold">Sl No</TableHead>
                            <TableHead className="font-semibold">Agent Type</TableHead>
                            <TableHead className="font-semibold">Integration Type</TableHead>
                            <TableHead className="font-semibold">Country Code</TableHead>
                            <TableHead className="font-semibold">Phone Number</TableHead>
                            <TableHead className="font-semibold text-center">Details</TableHead>
                            {canUpdate && (
                              <TableHead className="font-semibold text-center">Actions</TableHead>
                            )}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {integrations.map((integration, index) => {
                            const serialNumber = index + 1;
                            return (
                              <TableRow
                                key={integration.id || index}
                                className="transition-colors hover:bg-muted/50 cursor-pointer"
                              >
                                <TableCell className="font-medium">{serialNumber}</TableCell>
                                <TableCell>
                                  {AGENT_TYPE_LABELS[integration.agentType as keyof typeof AGENT_TYPE_LABELS] || integration.agentType}
                                </TableCell>
                                <TableCell>
                                  {INTEGRATION_TYPES.find(t => t.key === integration.intType)?.value || integration.intType}
                                </TableCell>
                                <TableCell>{integration.countryCode}</TableCell>
                                <TableCell>{integration.phoneNo}</TableCell>
                                <TableCell>
                                  <div className="flex justify-center">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDetailsClick(integration.details);
                                      }}
                                      title="View Details"
                                    >
                                      <Info className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                                {canUpdate && (
                                  <TableCell>
                                    <div className="flex gap-2 justify-center">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary transition-colors"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEditClick(integration);
                                        }}
                                        title="Edit Integration"
                                      >
                                        <Edit className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10 transition-colors"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteClick(integration);
                                        }}
                                        title="Delete Integration"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                )}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </div>

                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">Delete Communication Integration</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              Are you sure you want to delete this phone number mapping? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting} className="shadow-sm hover:shadow-md transition-all duration-200">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 text-white shadow-md hover:shadow-lg transition-all duration-200"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Integration Details</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <pre className="bg-muted p-4 rounded-md overflow-auto text-sm border border-border">
              {JSON.stringify(selectedDetails, null, 2)}
            </pre>
          </div>
        </DialogContent>
      </Dialog>

      {/* Form Dialog */}
      <Dialog open={formDialogOpen} onOpenChange={setFormDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-foreground">
              {editingIntegration ? "Edit" : "Add"} Phone Number Mapping
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <CommunicationIntegrationForm
              integration={editingIntegration}
              onSuccess={handleFormSuccess}
              onCancel={() => setFormDialogOpen(false)}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommunicationIntegrationPage;
